import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/lib/supabaseClient";
import type { OfferMethod, OfferPageData } from "@/types/sms";

const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/offer-page`;

/**
 * Plain `fetch` to the public offer-page edge function — no Clerk session
 * exists on this route (OP-1 "no third-party requests" also rules out the
 * authenticated Supabase client). The publishable anon key satisfies
 * Supabase's own gateway `apikey` requirement; offer-page itself has
 * `verify_jwt = false` and does its own IP rate limiting.
 */
async function fetchOfferPage(code: string): Promise<OfferPageData> {
  const response = await fetch(`${FUNCTION_URL}?code=${encodeURIComponent(code)}`, {
    headers: { apikey: SUPABASE_PUBLISHABLE_KEY },
  });
  if (!response.ok && response.status !== 429) {
    return { state: "unavailable" };
  }
  if (response.status === 429) {
    throw new Error("Too many requests. Try again in a moment.");
  }
  return (await response.json()) as OfferPageData;
}

export const useOfferPage = (code: string) => {
  return useQuery<OfferPageData>({
    queryKey: ["offer", code],
    queryFn: () => fetchOfferPage(code),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useStartRedemption = (code: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ method }: { method: OfferMethod }): Promise<OfferPageData> => {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_PUBLISHABLE_KEY },
        body: JSON.stringify({ code, action: "start", method }),
      });
      if (response.status === 429) throw new Error("Too many requests. Try again in a moment.");
      return (await response.json()) as OfferPageData;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["offer", code], data);
    },
  });
};
